/**
 * Mosque Information Donation Page (تفاصيل الطلب).
 * Reached from MosqueDonationDetailsPage via "تقديم الطلب".
 *
 * ✅ FIXES INCLUDED (NO GUESSWORK)
 * 1) ✅ Supplier ID mapping is SAFE:
 *    - Supports: id / supplierId / supplier_id / partner_id / Id / partnerId / data.partner_id
 *    - All validation + PATCH uses getSupplierIdSafe()
 *
 * 2) ✅ Kind detection is NOW CORRECT (HDR vs DDR):
 *    - Reads (in order): querystring kind → state.kind → state.source → requestName prefix (HDR-) → default DDR
 *    - Re-evaluates AFTER apiData loads (so you don’t get stuck with the “temporary” kind)
 *
 * 3) ✅ API calls are fixed:
 *    - Create HDR: hygieneDonationService.createHygieneSupplyRequest
 *    - Patch  HDR: hygieneDonationService.patchHygieneSupplyRequest
 *    - List   HDR: hygieneDonationService.listHygieneSupplyRequestsByDonor
 *    - Create DDR: submitMosqueSupplyRequest
 *    - Patch  DDR: patchSupplyRequest
 *    - List   DDR: listSupplyRequestsByDonor
 *
 * 4) ✅ Opportunity details loader:
 *    - Tries HDR detail function if it exists in hygieneDonationService (no compile break if not present)
 *    - Otherwise falls back to DDR detail API (so the page never crashes)
 *
 * ✅ IMPORTANT FIX (COMPILATION):
 * - Your previous code was calling `hygieneDonationService.*` BUT you did not import it.
 * - Now we import it as `* as hygieneDonationService` and removed the unused `getHygieneDdrById` import.
 *
 * NOTE:
 * - I did NOT change your UI structure/markup except the “My Requests” tab link to respect hygiene/maintenance.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleMapsLoader } from "../../maps/GoogleMapsLoader";
import { asErr } from "../../../utils/apiHelpers";
import { isAuthenticated, clearAuthToken } from "../../../services/authService";
import * as donorService from "../../../services/donorService";

import {
  getMosqueDonationOpportunityByRequestId,
  getMosqueImages,
  submitMosqueSupplyRequest,
  patchSupplyRequest,
  listSupplyRequestsByDonor,
} from "../../../services/mosqueDonationService";

// ✅ FIX: you were using hygieneDonationService.* without importing it
import * as hygieneDonationService from "../../../services/hygieneDonationService";

import type { SupplierListItem } from "../../../services/suppliersService";
import type { SupplyRequestListItem } from "../../../services/mosqueDonationService";
import type { MosqueDonationOpportunityDetail } from "../../../types/mosqueDonationOpportunity";
import type { DonorRow } from "../../../types/donor";
import Breadcrumbs, { type Crumb } from "../../../components/navigation/Breadcrumbs";
import "./MosqueInformationDonationPage.css";

import SupplierModal from "./components/SupplierModal";
import MosqueDonationJourneyStepper, {
  type JourneyStepDef,
  type MosqueJourneySectionId,
} from "./MosqueDonationJourneyStepper";

/** Donation/mosque item passed from MosqueDonationDetailsPage via navigation state (fallback when API not yet loaded) */
type DonationItem = {
  mosque_name?: string | null;
  mosqueName?: string | null;
  mosque_id?: number | null;
  mosqueId?: number | null;
  mosqueNumber?: string | number | null;
  serialNumber?: string | number | null;
  number?: string | number | null;

  request_number?: string | number | null;
  request_name?: string | null;
  request_id?: number | null;

  region_name?: string | null;
  regionName?: string | null;

  city_name?: string | null;
  cityName?: string | null;

  moia_center_name?: string | null;
  centerName?: string | null;

  district_name?: string | null;
  districtName?: string | null;
  neighborhoodName?: string | null;

  full_address?: string | null;
  fullAddress?: string | null;

  capacity?: number | null;
  women_prayer_room_capacity?: number | null;

  has_women_prayer_room?: string | boolean | null;
  hasWomenPrayerRoom?: string | boolean | null;
  hasWomenSection?: string | boolean | null;

  cat_a_name?: string | null;
  cat_b_name?: string | null;
  cat_c_name?: string | null;
  cat_d_name?: string | null;
  cat_e_names?: string | null;

  latitude?: number | null;
  longitude?: number | null;
};

type NavState =
  | {
      id?: string;
      item?: DonationItem;
      source?: "hygiene";
      kind?: "hdr" | "ddr";
    }
  | null;

/** Build partial API detail from nav-state item for fallback before API loads */
function itemToDetail(item: DonationItem | undefined): MosqueDonationOpportunityDetail | null {
  if (!item) return null;

  return {
    requestId: Number(item.request_id ?? 0),
    requestName: item.request_name ?? null,

    mosqueId: Number(item.mosque_id ?? item.mosqueId ?? 0),

    number:
      item.mosqueNumber != null || item.serialNumber != null || item.number != null
        ? String(item.mosqueNumber ?? item.serialNumber ?? item.number ?? "")
        : null,

    mosqueName: item.mosque_name ?? item.mosqueName ?? null,
    latitude: item.latitude ?? null,
    longitude: item.longitude ?? null,

    classificationId: null,
    classificationName: null,

    regionName: item.region_name ?? item.regionName ?? null,
    cityName: item.city_name ?? item.cityName ?? null,
    moiaCenterName: item.moia_center_name ?? item.centerName ?? null,
    fullAddress: item.full_address ?? item.fullAddress ?? null,
    districtName: item.district_name ?? item.districtName ?? item.neighborhoodName ?? null,

    catAId: null,
    catAName: item.cat_a_name ?? null,
    catBId: null,
    catBName: item.cat_b_name ?? null,
    catCId: null,
    catCName: item.cat_c_name ?? null,
    catDId: null,
    catDName: item.cat_d_name ?? null,

    mosqueOpeningDate: null,
    urbanCondition: null,
    dateMaintenanceLast: null,
    fridayPrayerRows: null,
    surface: null,
    landArea: null,
    lengthRowMenPraying: null,

    rowMenPrayingNumber: item.capacity ?? null,
    toiletMenNumber: null,
    menPrayerAvgAttendance: null,

    lengthRowWomenPraying: null,
    rowWomenPrayingNumber: null,
    womenPrayerRoomCapacity: item.women_prayer_room_capacity ?? null,

    isWomenToilets:
      item.has_women_prayer_room != null ||
      item.hasWomenPrayerRoom != null ||
      item.hasWomenSection != null
        ? String(item.has_women_prayer_room ?? item.hasWomenPrayerRoom ?? item.hasWomenSection ?? "")
        : null,

    toiletWomanNumber: null,

    internalDoorsNumber: null,
    internalHeadsetNumber: null,
    internalSpeakerNumber: null,

    hasExternalCamera: null,
    hasInternalCamera: null,

    externalDoorsNumbers: null,
    externalHeadsetNumber: null,
    externalSpeakerNumber: null,

    numMinarets: null,
    numFloors: null,
    hasBasement: null,

    mosqueRooms: null,
    nonBuildingArea: null,
    roofedArea: null,
    isFreeArea: null,

    hasAirConditioners: null,
    acType: null,
    numAirConditioners: null,
    numLightingInside: null,

    hasFireExtinguishers: null,
    hasFireSystemPumps: null,
    mosqueHistorical: null,

    catEIds: null,
    catENames: item.cat_e_names ?? null,
  };
}

const fmt = (v: string | number | null | undefined): string | number =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "")
    ? "—"
    : typeof v === "string"
      ? v.trim()
      : v;

const buildGoogleMapsLink = (lat?: number | null, lng?: number | null): string => {
  if (lat == null || lng == null) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}&hl=ar&z=16`;
};

const yesNoAr = (v?: string | boolean | null): string => {
  if (v === true) return "نعم";
  if (v === false) return "لا";

  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return "—";

  return ["1", "true", "t", "yes", "y", "نعم", "ok", "✓"].includes(s) ? "نعم" : "لا";
};

type MosqueDetailsTabId = "server" | "contact" | "otherWork" | "address" | "qualifications" | "employment";

const MOSQUE_DETAILS_TABS: { id: MosqueDetailsTabId; label: string }[] = [
  { id: "server", label: "بيانات المسجد" },
  { id: "contact", label: "صور المسجد" },
  { id: "otherWork", label: "حالة المسجد" },
  { id: "address", label: "مبنى المسجد" },
  { id: "qualifications", label: "دورات المياه" },
  { id: "employment", label: "الكهرباء و الصوتيات" },
];

const JOURNEY_STEPS: JourneyStepDef[] = [
  { key: "mosque", section: "mosque", label: "بيانات المسجد" },
  { key: "request", section: "requestDetails", label: "تفاصيل الطلب" },
  { key: "send", section: "send", label: "إرسال الطلب" },
];

function stageBadge(stage?: string) {
  const s = (stage || "").toLowerCase();

  if (s.includes("draft")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200">
        مسودة
      </span>
    );
  }
  if (s.includes("progress")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-sky-50 text-sky-700 ring-1 ring-sky-200">
        قيد المعالجة
      </span>
    );
  }
  if (s.includes("submitted")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-50 text-amber-800 ring-1 ring-amber-200">
        تم الإرسال
      </span>
    );
  }
  if (s.includes("approved") || s.includes("done")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
        معتمد
      </span>
    );
  }
  if (s.includes("reject")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-50 text-red-700 ring-1 ring-red-200">
        مرفوض
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-neutral-50 text-neutral-800 ring-1 ring-neutral-200">
      {stage || "غير معروف"}
    </span>
  );
}

/** ✅ Resolve kind (HDR/DDR) reliably from query + state + data */
function resolveKind(args: {
  qsKind?: string | null;
  navState: any;
  item?: any;
  data?: any;
}): "hdr" | "ddr" {
  const { qsKind, navState, item, data } = args;

  // 0) querystring is strongest (refresh-safe)
  if (qsKind === "hdr" || qsKind === "ddr") return qsKind;

  // 1) explicit state.kind
  const k = navState?.kind;
  if (k === "hdr" || k === "ddr") return k;

  // 2) existing state.source
  if (navState?.source === "hygiene") return "hdr";

  // 3) infer from request name (HDR-xxxx)
  const rn = data?.requestName ?? item?.request_name ?? item?.requestName ?? "";
  if (typeof rn === "string" && rn.toUpperCase().startsWith("HDR-")) return "hdr";

  // 4) default
  return "ddr";
}

export default function MosqueInformationDonationPage() {
  const [activeSection, setActiveSection] = useState<MosqueJourneySectionId>("mosque");
  const [detailsTab, setDetailsTab] = useState<MosqueDetailsTabId>("server");
  const location = useLocation();
  const navigate = useNavigate();
  const navState = (location.state as NavState) || null;

  const item: DonationItem | undefined = navState?.item;
  const opportunityIdFromState = navState?.id;

  // ✅ read query params once per render (refresh-safe)
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const opportunityIdFromQuery = qs.get("id");
  const kindFromQuery = qs.get("kind"); // "hdr" | "ddr" | null

  const opportunityId = opportunityIdFromState ?? opportunityIdFromQuery ?? undefined;

  // Map container ref (we render map only when needed)
  const mapRef = useRef<HTMLDivElement | null>(null);

  // ---- API detail state ----
  const [apiData, setApiData] = useState<MosqueDonationOpportunityDetail | null>(null);
  const [loadingApi, setLoadingApi] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // ---- Images tab state ----
  const [mosqueImages, setMosqueImages] = useState<{ internalUrls: string[]; externalUrls: string[] } | null>(null);
  const [loadingMosqueImages, setLoadingMosqueImages] = useState(false);
  const [mosqueImagesError, setMosqueImagesError] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // ---- Donor / submit state ----
  const [donors, setDonors] = useState<DonorRow[]>([]);
  const [selectedDonorToken, setSelectedDonorToken] = useState<string>("");
  const [loadingDonors, setLoadingDonors] = useState(false);

  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sessionExpiredOfferRelogin, setSessionExpiredOfferRelogin] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  // ---- Requests view ----
  const [showRequestsView, setShowRequestsView] = useState(false);
  const [mosqueRequests, setMosqueRequests] = useState<SupplyRequestListItem[]>([]);
  const [loadingMosqueReqs, setLoadingMosqueReqs] = useState(false);
  const [mosqueReqsError, setMosqueReqsError] = useState<string | null>(null);

  // ---- Supplier modal state ----
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierListItem | null>(null);

  // keep as-is (your business UX)
  const [userPerformsMaintenance, setUserPerformsMaintenance] = useState(false);
  const [maintenanceDate, setMaintenanceDate] = useState("");

  const loggedIn = isAuthenticated();

  // =========================
  // ✅ SAFE SUPPLIER ID
  // =========================
  const getSupplierIdSafe = (s: SupplierListItem | null): number | undefined => {
    if (!s) return undefined;
    const obj = s as any;

    // ✅ IMPORTANT: support multiple shapes from list/create APIs
    const raw =
      obj.supplierId ??
      obj.supplier_id ??
      obj.partner_id ??
      obj.partnerId ??
      obj.id ??
      obj.Id ??
      obj.ID ??
      obj.data?.partner_id ??
      obj.data?.supplierId ??
      obj.data?.id;

    const id = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : undefined;
    return id && id > 0 ? id : undefined;
  };

  // -----------------------------
  // Resolve requestId/opportunityId
  // -----------------------------
  const requestId =
    item?.request_id ??
    (item as { Request_Id?: number } | undefined)?.Request_Id ??
    (opportunityId ? Number(opportunityId) : 0);

  const effectiveOpportunityId = opportunityId ?? (requestId ? String(requestId) : undefined);

  // Use API data if available, otherwise fallback to navigation-state item (so page never hard-crashes)
  const data: MosqueDonationOpportunityDetail | null = apiData ?? itemToDetail(item);

  // ✅ FINAL kind (updates after apiData loads)
  const kindFinal = useMemo(
    () => resolveKind({ qsKind: kindFromQuery, navState, item, data }),
    // we re-evaluate when apiData changes to properly infer "HDR-" prefix from loaded data
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kindFromQuery, navState, apiData, item]
  );
  const isHygieneSource = kindFinal === "hdr";

  // ---------------------------------------------
  // Journey UI state (no business-logic changes)
  // ---------------------------------------------
  const supplierIdForUi = (getSupplierIdSafe(selectedSupplier) ?? 0) as number;
  const donorOk = !!selectedDonorToken?.trim();
  const selfOk = !!userPerformsMaintenance && !!maintenanceDate?.trim();
  const supplierOk = supplierIdForUi > 0 && donorOk;
  const requestDetailsReady = supplierOk || selfOk;

  const submitActionDisabled = useMemo(
    () => submitLoading || !loggedIn || !confirmChecked || !requestDetailsReady,
    [submitLoading, loggedIn, confirmChecked, requestDetailsReady]
  );

  const completedSteps = useMemo(() => {
    const baseReady = !!data;
    return [
      baseReady,
      baseReady && requestDetailsReady,
      baseReady && !submitLoading && !submitError && !!submitSuccess,
    ];
  }, [data, requestDetailsReady, submitLoading, submitError, submitSuccess]);

  const gotoSection = (id: MosqueJourneySectionId) => {
    if (id === "send" && !requestDetailsReady) {
      setActiveSection("requestDetails");
      return;
    }
    setActiveSection(id);
  };

  // ---------------------------------------------
  // Helpers (supplyId resolution + patching)
  // ---------------------------------------------
  const tryExtractSupplyId = (createResult: any): number | undefined => {
    // Some APIs return { data: { id } }, others return { id } directly
    const dataObj = (createResult?.data ?? createResult?.result ?? createResult) as any;

    const raw =
      createResult?.supply_id ??
      createResult?.supplyId ??
      createResult?.SupplyId ??
      createResult?.id ??
      createResult?.Id ??
      createResult?.requestId ??
      createResult?.Request_Id ??
      dataObj?.supply_id ??
      dataObj?.supplyId ??
      dataObj?.SupplyId ??
      dataObj?.id ??
      dataObj?.Id ??
      dataObj?.requestId ??
      dataObj?.Request_Id;

    const id = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : undefined;
    return id && id > 0 ? id : undefined;
  };

  /**
   * ✅ If create does not return supplyId (common case), we list by donor and match DDR/HDR id
   * so that we can PATCH using supply_id.
   */
  const resolveSupplyIdByListing = async (opts: {
    isHygiene: boolean;
    donorIdToken: string;
    ddrId: number;
  }): Promise<number | undefined> => {
    const { isHygiene, donorIdToken, ddrId } = opts;

    if (!donorIdToken?.trim()) return undefined;

    try {
      const listRes = isHygiene
        ? await hygieneDonationService.listHygieneSupplyRequestsByDonor({
            DonorIdToken: donorIdToken.trim(),
            PageNo: 1,
            PageSize: 100,
          })
        : await listSupplyRequestsByDonor({
            DonorIdToken: donorIdToken.trim(),
            PageNo: 1,
            PageSize: 100,
          });

      const list = (listRes?.data ?? []) as any[];

      // match by DDR/HDR id if possible, fallback to first row (best effort)
      const match =
        list.find((r) => Number(r.ddrId ?? r.requestId ?? r.DDR_Id ?? r.ddr_id) === ddrId) ?? list[0];

      const raw = match?.supplyId ?? match?.supply_id ?? match?.id;
      const id = raw != null && Number.isFinite(Number(raw)) ? Number(raw) : undefined;
      return id && id > 0 ? id : undefined;
    } catch {
      return undefined;
    }
  };

  /**
   * ✅ PATCH is only needed when:
   * - supplier is selected OR
   * - self-provider is checked
   */
  const shouldPatch = (args: { supplierId?: number; isSelfProvider: boolean }) => {
    if (args.isSelfProvider) return true;
    if (args.supplierId && args.supplierId > 0) return true;
    return false;
  };

  // -----------------------------
  // Load donors for logged-in user
  // -----------------------------
  useEffect(() => {
    if (!loggedIn) return;

    let mounted = true;

    (async () => {
      try {
        setLoadingDonors(true);

        const list = donorService.scopeDonorsToCurrentUser(
          (await donorService.listMine()) as DonorRow[]
        );
        if (!mounted) return;

        setDonors(list);

        const ownerDonorToken = list[0]?.donorIdToken ?? "";
        setSelectedDonorToken(ownerDonorToken);
      } catch {
        if (mounted) setDonors([]);
      } finally {
        if (mounted) setLoadingDonors(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [loggedIn]);

  // -------------------------------------------
  // Load request list (when "showRequestsView")
  // -------------------------------------------
  useEffect(() => {
    // When user is not in requests view OR not logged in OR no donor selected → reset list and exit
    if (!showRequestsView || !loggedIn || !selectedDonorToken) {
      setMosqueRequests([]);
      setMosqueReqsError(null);
      return;
    }

    let closed = false;

    (async () => {
      try {
        setLoadingMosqueReqs(true);
        setMosqueReqsError(null);

        // ✅ Correct list API based on source (HDR vs DDR)
        const result = isHygieneSource
          ? await hygieneDonationService.listHygieneSupplyRequestsByDonor({
              DonorIdToken: selectedDonorToken,
              PageNo: 1,
              PageSize: 100,
            })
          : await listSupplyRequestsByDonor({
              DonorIdToken: selectedDonorToken,
              PageNo: 1,
              PageSize: 100,
            });

        if (!closed) setMosqueRequests((result?.data ?? []) as any);
      } catch (e) {
        if (!closed) {
          setMosqueReqsError(
            asErr(e, isHygieneSource ? "تعذر تحميل طلبات النظافة والتعقيم." : "تعذر تحميل طلبات المساجد.").message
          );
          setMosqueRequests([]);
        }
      } finally {
        if (!closed) setLoadingMosqueReqs(false);
      }
    })();

    return () => {
      closed = true;
    };
  }, [showRequestsView, loggedIn, selectedDonorToken, isHygieneSource]);

  // -----------------------------
  // ✅ Load opportunity detail by requestId
  // -----------------------------
  useEffect(() => {
    if (!requestId || requestId <= 0) {
      setApiData(null);
      return;
    }

    let mounted = true;

    setLoadingApi(true);
    setApiError(null);

    const load = async () => {
      try {
        /**
         * ✅ HDR detail loader:
         * We do NOT assume you have a hygiene detail endpoint in FE service.
         * So we do safe dynamic check:
         * - if exists: hygieneDonationService.getMosqueHygieneDonationOpportunityByRequestId(requestId)
         * - else: fallback to DDR detail loader
         */
        if (isHygieneSource) {
          const fn = (hygieneDonationService as any).getMosqueHygieneDonationOpportunityByRequestId;
          if (typeof fn === "function") {
            const detail = await fn(requestId);
            if (mounted) setApiData(detail);
            return;
          }
        }

        // ✅ Always available fallback (never crash)
        const detail = await getMosqueDonationOpportunityByRequestId(requestId);
        if (mounted) setApiData(detail);
      } catch (e) {
        if (mounted) {
          setApiError(asErr(e, "تعذر تحميل بيانات المسجد").message);
          setApiData(null);
        }
      } finally {
        if (mounted) setLoadingApi(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [requestId, isHygieneSource]);

  // -----------------------------
  // Map coordinates
  // -----------------------------
  const lat = data?.latitude;
  const lng = data?.longitude;
  const hasCoords = lat != null && lng != null;
  const googleMapsUrl = buildGoogleMapsLink(lat, lng);

  // Render Google Map only when Mosque step + "بيانات المسجد" tab active
  useEffect(() => {
    if (activeSection !== "mosque" || detailsTab !== "server" || !hasCoords || !mapRef.current) return;

    let map: google.maps.Map | null = null;

    GoogleMapsLoader.load("ar", "SA")
      .then((google) => {
        if (!mapRef.current) return;

        const center = { lat: Number(lat), lng: Number(lng) };

        map = new google.maps.Map(mapRef.current, {
          center,
          zoom: 14,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
          zoomControl: true,
        });

        new google.maps.Marker({ position: center, map });
      })
      .catch(() => {});

    return () => {
      map = null;
    };
  }, [activeSection, detailsTab, lat, lng, hasCoords]);

  // Load mosque images only when Mosque step + "صور المسجد" selected
  useEffect(() => {
    const mosqueId = data?.mosqueId != null ? Number(data.mosqueId) : 0;
    if (activeSection !== "mosque" || detailsTab !== "contact" || !mosqueId || mosqueId <= 0) return;

    let closed = false;

    setMosqueImagesError(null);
    setLoadingMosqueImages(true);

    getMosqueImages(mosqueId)
      .then((result) => {
        if (!closed) setMosqueImages(result);
      })
      .catch((e) => {
        if (!closed) {
          setMosqueImagesError(asErr(e, "تعذر تحميل صور المسجد").message);
          setMosqueImages(null);
        }
      })
      .finally(() => {
        if (!closed) setLoadingMosqueImages(false);
      });

    return () => {
      closed = true;
    };
  }, [activeSection, detailsTab, data?.mosqueId]);

  // -----------------------------
  // Derived UI values
  // -----------------------------
  const mosqueNumber = data?.number ?? "—";
  const mosqueName = data?.mosqueName ?? "—";

  // NOTE: Keep this as-is: it displays DDR-xxxx if requestName missing
  const contributionSerial =
    data?.requestName ?? (data?.requestId != null ? `DDR-${String(data.requestId).padStart(8, "0")}` : "—");

  const categoryTags = [
    { label: "A", value: data?.catAName },
    { label: "B", value: data?.catBName },
    { label: "C", value: data?.catCName },
    { label: "D", value: data?.catDName },
    { label: "E", value: data?.catENames },
  ].filter((t) => t.value != null && String(t.value).trim() !== "");

  const region = data?.regionName ?? "—";
  const city = data?.cityName ?? "—";
  const center = data?.moiaCenterName ?? "—";
  const district = data?.districtName ?? "—";
  const fullAddress = data?.fullAddress ?? "—";

  const yesNoBadge = (v?: string | boolean | null) => {
    const label = yesNoAr(v);
    if (label === "نعم") {
      return (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
          نعم
        </span>
      );
    }
    if (label === "لا") {
      return (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-red-50 text-red-700 ring-1 ring-red-200">
          لا
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200">
        —
      </span>
    );
  };

  const womenSection = yesNoBadge(data?.isWomenToilets);

  const renderRow = (label: string, value: React.ReactNode, index: number) => (
    <div key={`${label}-${index}`} className={`info-donation-row ${index % 2 === 1 ? "info-donation-row-alt" : ""}`}>
      <span className="info-donation-row-label">{label}:</span>
      <span className="info-donation-row-value">{value}</span>
    </div>
  );

  // tab label not needed; UI relies on tabs only

  // -----------------------------
  // Breadcrumbs
  // -----------------------------
  const crumbs: Crumb[] = useMemo(() => {
    const base: Crumb[] = [
      { label: "الرئيسية", to: "/" },
      { label: "الخدمات", to: "/services" },
      { label: "فرص عقود الصيانة", to: "/service/mosque-contribution" },
    ];

    if (effectiveOpportunityId) {
      base.push({
        label: "تفاصيل المسجد",
        to: `/mosque/donation-details/${effectiveOpportunityId}`,
        state: {
          item,
          backTo: "/mosque/donation-opportunities",
          ...(isHygieneSource ? { source: "hygiene" as const, kind: "hdr" as const } : { kind: "ddr" as const }),
        },
      });
    }

    base.push({ label: "تقديم الطلب", to: null });
    return base;
  }, [effectiveOpportunityId, item, isHygieneSource]);

  // -----------------------------
  // ✅ Submit request handler (HDR/DDR Correct)
  // -----------------------------
  const handleSubmitRequest = async () => {
    // reset UI messages first
    setSubmitError(null);
    setSessionExpiredOfferRelogin(false);
    setSubmitSuccess(null);

    // 1) auth checks
    if (!loggedIn) {
      setSubmitError("يجب تسجيل الدخول أولاً.");
      return;
    }

    // 2) confirm checkbox
    if (!confirmChecked) {
      setSubmitError("يرجى الإقرار بصحة البيانات قبل الإرسال.");
      return;
    }

    // 3) ids
    const DDR_id = Number(data?.requestId ?? 0);
    const mosque_id = Number(data?.mosqueId ?? 0);

    if (!DDR_id || DDR_id <= 0) {
      setSubmitError("تعذر تحديد رقم طلب المساهمة.");
      return;
    }
    if (!mosque_id || mosque_id <= 0) {
      setSubmitError("تعذر تحديد رقم المسجد.");
      return;
    }
    if (!effectiveOpportunityId) {
      setSubmitError("تعذر تحديد فرصة التبرع.");
      return;
    }

    // 4) donor + supplier + self provider
    const DonorIdToken = selectedDonorToken?.trim() ?? "";
    const hasDonor = !!DonorIdToken;

    const isSelfProvider = !!userPerformsMaintenance;

    const supplierId = getSupplierIdSafe(selectedSupplier);
    const hasSupplier = supplierId != null && supplierId > 0;

    // Business rule: supplier selected → donor required
    if (hasSupplier && !hasDonor) {
      setSubmitError("لتأكيد الطلب يرجى اختيار المستفيد (المساهم) مع متعهد الصيانة.");
      return;
    }

    // At least one of donor/supplier/self-provider must be chosen
    if (!hasDonor && !hasSupplier && !isSelfProvider) {
      setSubmitError("يرجى اختيار المستفيد أو متعهد الصيانة أو اختيار «أنا من سينفذ الصيانة».");
      return;
    }

    // supplier OR self-provider requires date
    if ((hasSupplier || isSelfProvider) && !maintenanceDate?.trim()) {
      setSubmitError(isHygieneSource ? "يرجى اختيار تاريخ تنفيذ الأعمال." : "يرجى اختيار تاريخ تنفيذ الصيانة.");
      return;
    }

    // date must be today or future
    if (maintenanceDate?.trim()) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const selectedDate = new Date(maintenanceDate.trim());
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        setSubmitError(
          isHygieneSource
            ? "يرجى اختيار تاريخ تنفيذ الأعمال من تاريخ اليوم أو تاريخ مستقبلي."
            : "يرجى اختيار تاريخ تنفيذ الصيانة من تاريخ اليوم أو تاريخ مستقبلي."
        );
        return;
      }
    }

    // If supplier object exists but we couldn't extract id → block (prevents broken PATCH payload)
    if (selectedSupplier && !hasSupplier) {
      setSubmitError("يرجى اختيار متعهد الصيانة من نتائج البحث أو إنشاء شركة جديدة.");
      return;
    }

    try {
      setSubmitLoading(true);

      // -----------------------------------
      // 1) POST create request (HDR vs DDR)
      // -----------------------------------
      let createResult: any;

      const supplierName = selectedSupplier?.name ?? (selectedSupplier as any)?.Name;
      const supplierNameSafe = typeof supplierName === "string" && supplierName.trim() ? supplierName.trim() : undefined;

      if (isHygieneSource) {
        // ✅ HDR create payload
        const hygienePayload: any = {
          DonorIdToken,
          DDR_Id: DDR_id, // this is "HDR's ddr reference" field per your API naming
          mosque_id,
        };

        if (hasSupplier) hygienePayload.supplier_id = supplierId;
        if (supplierNameSafe) hygienePayload.supplier_name = supplierNameSafe;
        if (maintenanceDate.trim()) hygienePayload.maintenance_date = maintenanceDate.trim();
        if (isSelfProvider) hygienePayload.is_self_provider = true;

        createResult = await hygieneDonationService.createHygieneSupplyRequest(hygienePayload);
      } else {
        // ✅ DDR create payload
        const payload: any = { DDR_id, DonorIdToken, mosque_id };

        if (hasSupplier) payload.supplier_id = supplierId;
        if (supplierNameSafe) payload.supplier_name = supplierNameSafe;
        if (maintenanceDate.trim()) payload.maintenance_date = maintenanceDate.trim();
        if (isSelfProvider) payload.is_self_provider = true;

        createResult = await submitMosqueSupplyRequest(payload);
      }

      // -----------------------------
      // 2) Resolve supplyId
      // -----------------------------
      let supplyId = tryExtractSupplyId(createResult);

      // if API didn't return supply_id, list and match
      if (!supplyId) {
        supplyId = await resolveSupplyIdByListing({
          isHygiene: isHygieneSource,
          donorIdToken: DonorIdToken,
          ddrId: DDR_id,
        });
      }

      // still not found → show success message but direct user to requests tab
      if (!supplyId) {
        setSubmitSuccess("تم إنشاء الطلب، لكن تعذر تحديد رقم الطلب لإكمال التحديث. يرجى التحقق من صفحة «طلباتي».");
        setShowRequestsView(true);

        const query = DonorIdToken ? `donor=${encodeURIComponent(DonorIdToken)}&` : "";
        navigate(`/my-requests?${query}tab=${isHygieneSource ? "hygiene" : "maintenance"}`, { replace: true });
        return;
      }

      // -----------------------------
      // 3) PATCH only when needed
      // -----------------------------
      const doPatch = shouldPatch({ supplierId, isSelfProvider });

      if (doPatch) {
        const contractDate = maintenanceDate.trim() || undefined;

        if (isHygieneSource) {
          // ✅ HDR PATCH
          await hygieneDonationService.patchHygieneSupplyRequest({
            supply_id: supplyId,
            contract_date: contractDate,
            supplier_id: hasSupplier ? supplierId : undefined,
            is_self_provider: isSelfProvider ? true : undefined,
          });
        } else {
          // ✅ DDR PATCH
          await patchSupplyRequest({
            supply_id: supplyId,
            contract_date: contractDate,
            supplier_id: hasSupplier ? supplierId : undefined,
            is_self_provider: isSelfProvider ? true : undefined,
          });
        }
      }

      // success
      setSubmitSuccess("تم إرسال الطلب بنجاح.");
      setShowRequestsView(true);

      const query = DonorIdToken ? `donor=${encodeURIComponent(DonorIdToken)}&` : "";
      navigate(`/my-requests?${query}tab=${isHygieneSource ? "hygiene" : "maintenance"}`, { replace: true });
    } catch (e) {
      const err = asErr(e, "تعذر إرسال الطلب");
      const msg = err.message;

      // if backend returns session-expired message, show relogin CTA
      const isSessionExpired = /odoo\s*session\s*expired|session\s*expired/i.test(msg);

      if (isSessionExpired) {
        setSubmitError("انتهت صلاحية الجلسة. يرجى تسجيل الخروج ثم تسجيل الدخول مرة أخرى ثم إعادة إرسال الطلب.");
        setSessionExpiredOfferRelogin(true);
      } else {
        setSubmitError(msg);
        setSessionExpiredOfferRelogin(false);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // =============================
  // RENDER
  // =============================
  return (
    <div className="mosque-info-donation-page bg-gray-50 min-h-screen" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="mb-6">
          <Breadcrumbs items={crumbs} />
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white/90 shadow-xl overflow-hidden">
          {/* Header */}
          <div className="px-4 md:px-6 py-6 border-b border-gray-100 bg-white/75 backdrop-blur">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h1 className="text-2xl font-semibold text-neutral-900">تقديم الطلب</h1>

              <div className="flex items-center gap-2">
                {effectiveOpportunityId && (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-xl border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
                    onClick={() =>
                      navigate(`/mosque/donation-details/${effectiveOpportunityId}`, {
                        state: {
                          item,
                          backTo: "/mosque/donation-opportunities",
                          ...(isHygieneSource
                            ? { source: "hygiene" as const, kind: "hdr" as const }
                            : { kind: "ddr" as const }),
                        },
                      })
                    }
                  >
                    رجوع
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Journey stepper (matches land journey style) */}
          <div className="px-4 md:px-6 py-4 border-b border-gray-100 bg-slate-50/60">
            <MosqueDonationJourneyStepper
              steps={JOURNEY_STEPS}
              activeSection={activeSection}
              completed={completedSteps}
              onSelectSection={gotoSection}
            />
          </div>

          {/* Main */}
          <main className="mosque-info-donation-page-main px-4 md:px-6 py-6">
            {/* SUCCESS VIEW */}
            {data && showRequestsView ? (
              <div className="space-y-6">
                <div className="info-donation-card p-4 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-800">
                  <p className="font-semibold">{submitSuccess}</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-xl border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
                    onClick={() => setShowRequestsView(false)}
                  >
                    رجوع إلى النموذج
                  </button>

                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-xl bg-sa-600 px-4 py-2 text-sm font-medium text-white hover:bg-sa-700"
                    onClick={() => navigate("/mosque/donation-opportunities")}
                  >
                    تقديم طلب جديد
                  </button>

                  {/* ✅ FIX: tab depends on kind */}
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-xl border border-sa-600 px-4 py-2 text-sm font-medium text-sa-700 hover:bg-sa-50"
                    onClick={() => navigate(`/my-requests?tab=${isHygieneSource ? "hygiene" : "maintenance"}`)}
                  >
                    طلباتي
                  </button>

                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-xl border border-neutral-400 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
                    onClick={() => navigate("/my-dashboard")}
                  >
                    الذهاب للوحة المؤشرات
                  </button>
                </div>

                <h2 className="info-donation-section-title">عرض كل الطلبات</h2>

                <div className="info-donation-card">
                  {loggedIn ? (
                    <>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-end mb-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">اسم المستفيد</label>

                          <div className="flex items-center gap-2">
                            <select
                              className="block flex-1 min-w-0 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sa-300"
                              dir="rtl"
                              value={selectedDonorToken}
                              onChange={(e) => setSelectedDonorToken(e.target.value)}
                              disabled={loadingDonors}
                            >
                              {donors.map((d) => (
                                <option key={d.donorIdToken} value={d.donorIdToken}>
                                  {d.name}
                                </option>
                              ))}
                            </select>

                            <button
                              type="button"
                              className="inline-flex items-center justify-center rounded-xl border border-sa-400 bg-sa-50 px-3 py-2 text-sm font-medium text-sa-800 hover:bg-sa-100 focus:outline-none focus:ring-2 focus:ring-sa-500 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-sa-50"
                              onClick={() => setSupplierModalOpen(true)}
                              disabled={userPerformsMaintenance}
                            >
                              متعهد الصيانة
                            </button>

                            <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-neutral-700 dark:text-neutral-300">
                              <input
                                type="checkbox"
                                checked={userPerformsMaintenance}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setUserPerformsMaintenance(checked);
                                  if (checked) setSelectedSupplier(null);
                                }}
                                className="h-4 w-4 rounded border-neutral-300 text-sa-600 focus:ring-sa-500"
                              />
                              <span>أنا من سينفذ الصيانة</span>
                            </label>

                            <div className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                              <label htmlFor="maintenance-date-list" className="whitespace-nowrap">
                                تاريخ تنفيذ الصيانة
                              </label>
                              <input
                                id="maintenance-date-list"
                                type="date"
                                value={maintenanceDate}
                                onChange={(e) => setMaintenanceDate(e.target.value)}
                                min={new Date().toISOString().split("T")[0]}
                                className="rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
                              />
                            </div>
                          </div>

                          {selectedSupplier && (
                            <div className="mt-3 rounded-xl border border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800/50 p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                                  بيانات متعهد الصيانة
                                </span>

                                <button
                                  type="button"
                                  className="text-red-600 hover:text-red-700 text-sm underline focus:outline-none"
                                  onClick={() => setSelectedSupplier(null)}
                                >
                                  إلغاء
                                </button>
                              </div>

                              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                <dt className="text-neutral-500 dark:text-neutral-400">اسم الشركة</dt>
                                <dd className="text-neutral-900 dark:text-neutral-100 font-medium">
                                  {selectedSupplier.name ?? "—"}
                                </dd>

                                <dt className="text-neutral-500 dark:text-neutral-400">رقم الشركة</dt>
                                <dd className="text-neutral-900 dark:text-neutral-100 font-medium">
                                  {getSupplierIdSafe(selectedSupplier) ?? "—"}
                                </dd>

                                {selectedSupplier.email ? (
                                  <>
                                    <dt className="text-neutral-500 dark:text-neutral-400">البريد الإلكتروني</dt>
                                    <dd className="text-neutral-900 dark:text-neutral-100">{selectedSupplier.email}</dd>
                                  </>
                                ) : null}

                                {selectedSupplier.phone ? (
                                  <>
                                    <dt className="text-neutral-500 dark:text-neutral-400">رقم الجوال</dt>
                                    <dd className="text-neutral-900 dark:text-neutral-100">{selectedSupplier.phone}</dd>
                                  </>
                                ) : null}

                                {selectedSupplier.commercialRegistrationNo ? (
                                  <>
                                    <dt className="text-neutral-500 dark:text-neutral-400">السجل التجاري</dt>
                                    <dd className="text-neutral-900 dark:text-neutral-100">
                                      {selectedSupplier.commercialRegistrationNo}
                                    </dd>
                                  </>
                                ) : null}
                              </dl>
                            </div>
                          )}
                        </div>

                        <div className="rounded-2xl border border-gray-200 bg-sa-50 p-3 text-sm text-neutral-700">
                          عدد الطلبات:{" "}
                          <span className="font-semibold text-neutral-900">
                            {loadingMosqueReqs ? "…" : mosqueRequests.length}
                          </span>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
                        <div className="px-5 py-4 border-b border-neutral-200 text-base font-semibold text-neutral-800 text-center">
                          {isHygieneSource ? "طلبات أعمال النظافة والتعقيم القائمة" : "طلبات عقود الصيانة القائمة"}
                        </div>

                        <div className="p-5">
                          {mosqueReqsError && (
                            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm mb-4">
                              {mosqueReqsError}
                            </div>
                          )}

                          {loadingMosqueReqs && <div className="text-neutral-500">جاري التحميل...</div>}

                          {!loadingMosqueReqs && mosqueRequests.length === 0 && !mosqueReqsError && (
                            <div className="text-neutral-500">
                              {isHygieneSource ? "لا توجد طلبات نظافة وتعقيم" : "لا توجد طلبات مساجد"}
                            </div>
                          )}

                          {!loadingMosqueReqs && mosqueRequests.length > 0 && (
                            <div className="overflow-x-auto rounded-xl border border-neutral-200 w-full max-w-full">
                              <table className="w-full min-w-[800px] text-sm">
                                <thead>
                                  <tr className="text-center bg-neutral-50 text-neutral-600">
                                    <th className="py-2.5 px-3 font-medium">رقم الطلب</th>
                                    <th className="py-2.5 px-3 font-medium">رقم المسجد</th>
                                    <th className="py-2.5 px-3 font-medium">اسم المسجد</th>
                                    <th className="py-2.5 px-3 font-medium">التاريخ</th>
                                    <th className="py-2.5 px-3 font-medium">حالة الطلب</th>
                                    <th className="py-2.5 px-3 font-medium">اسم مقدم الطلب</th>
                                  </tr>
                                </thead>

                                <tbody className="divide-y divide-neutral-200">
                                  {mosqueRequests.map((row: any) => (
                                    <tr
                                      key={row.supplyId ?? row.supply_id ?? row.id}
                                      className="hover:bg-neutral-50 align-top"
                                    >
                                      <td className="py-2.5 px-3 text-center text-neutral-900 whitespace-nowrap">
                                        {row.supplyName ?? row.requestName ?? `DSR-${row.supplyId ?? row.id ?? "—"}`}
                                      </td>

                                      <td className="py-2.5 px-3 text-center text-neutral-800 whitespace-nowrap">
                                        {row.number ?? "—"}
                                      </td>

                                      <td className="py-2.5 px-3 text-center text-neutral-800 max-w-[200px]">
                                        <span className="block leading-snug break-words" title={row.mosqueName ?? undefined}>
                                          {row.mosqueName ?? "—"}
                                        </span>
                                      </td>

                                      <td className="py-2.5 px-3 text-center text-neutral-700 whitespace-nowrap">
                                        {row.supplyDate ?? "—"}
                                      </td>

                                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                        {stageBadge(row.state ?? row.status)}
                                      </td>

                                      <td className="py-2.5 px-3 text-center text-neutral-700 max-w-[220px]">
                                        <span className="block leading-snug break-words" title={row.partnerName ?? undefined}>
                                          {row.partnerName ?? "—"}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-neutral-600">سجّل الدخول لعرض الطلبات.</p>
                  )}
                </div>
              </div>
            ) : (
              // FORM VIEW
              <>
                {loadingApi && !data && (
                  <div className="info-donation-card p-6 text-center text-neutral-600">جاري تحميل بيانات المسجد...</div>
                )}

                {apiError && !data && <div className="info-donation-card p-6 text-center text-red-600">{apiError}</div>}

                {!loadingApi && !data && !apiError && (
                  <div className="info-donation-card p-6 text-center text-neutral-600">
                    <p className="mb-4">يجب الوصول لهذه الصفحة من صفحة تفاصيل فرصة التبرع (زر «تقديم الطلب»).</p>
                    <button
                      type="button"
                      className="info-donation-map-btn"
                      onClick={() => navigate("/mosque/donation-opportunities")}
                    >
                      الذهاب إلى فرص التبرع
                    </button>
                  </div>
                )}

                {data && activeSection === "mosque" && (
                  <div className="mb-4 flex flex-wrap items-center justify-center gap-2 rounded-2xl border border-neutral-200 bg-white/80 px-3 py-3">
                    {MOSQUE_DETAILS_TABS.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                          detailsTab === tab.id
                            ? "bg-sa-100 text-sa-700 shadow-inner border border-sa-200"
                            : "text-gray-700 hover:bg-gray-100 border border-transparent"
                        }`}
                        onClick={() => setDetailsTab(tab.id)}
                        aria-pressed={detailsTab === tab.id}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* TAB: تفاصيل الطلب */}
                {data && (activeSection === "requestDetails" || activeSection === "send") && (
                  <div className="info-donation-request-details" dir="rtl">
                    <div className="info-donation-card">
                      {renderRow(
                        "الرقم التسلسلي للمسجد",
                        <span dir="ltr" className="inline-block text-neutral-900">
                          {mosqueNumber}
                        </span>,
                        0
                      )}
                      {renderRow("اسم المسجد", mosqueName, 1)}
                      {renderRow("التصنيف", fmt(data.classificationName), 2)}
                      {renderRow(
                        "رقم الطلب (المساهمة)",
                        <span dir="ltr" className="inline-block text-neutral-900">
                          {contributionSerial}
                        </span>,
                        3
                      )}
                      {renderRow(
                        "اسم الطلب",
                        <span dir="ltr" className="inline-block text-neutral-900">
                          {fmt(data.requestName)}
                        </span>,
                        4
                      )}

                      <div className={`info-donation-row info-donation-row-no-label ${5 % 2 === 1 ? "info-donation-row-alt" : ""}`}>
                        <span className="info-donation-row-value">
                          {categoryTags.length > 0 ? (
                            <div className="info-donation-category-list">
                              {categoryTags.map((tag) => (
                                <div key={tag.label} className="info-donation-category-item">
                                  <span className="info-donation-category-badge">{tag.label}</span>
                                  <span className="info-donation-category-text">{tag.value}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            "—"
                          )}
                        </span>
                      </div>

                    {effectiveOpportunityId && (
                      <div className="info-donation-submit-wrap">
                        {loggedIn && (
                          <>
                            <div className="info-donation-row">
                              <span className="info-donation-row-label">المستفيد:</span>
                              <span className="info-donation-row-value" dir="rtl">
                                <select
                                  className="info-donation-select w-full"
                                  value={selectedDonorToken}
                                  onChange={(e) => setSelectedDonorToken(e.target.value)}
                                  disabled={loadingDonors}
                                >
                                  <option value="">— اختر المستفيد —</option>
                                  {donors.map((d) => (
                                    <option key={d.donorIdToken} value={d.donorIdToken}>
                                      {d.name || d.donorIdToken}
                                    </option>
                                  ))}
                                </select>
                              </span>
                            </div>

                            <div className="info-donation-row">
                              <span className="info-donation-row-label">طريقة التنفيذ:</span>
                              <span className="info-donation-row-value flex flex-wrap items-center gap-3" dir="rtl">
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center rounded-xl border border-sa-400 bg-sa-50 px-4 py-2 text-sm font-medium text-sa-800 hover:bg-sa-100 focus:outline-none focus:ring-2 focus:ring-sa-500 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-sa-50"
                                  onClick={() => setSupplierModalOpen(true)}
                                  disabled={userPerformsMaintenance}
                                >
                                  متعهد الصيانة
                                </button>

                                <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-neutral-700 dark:text-neutral-300">
                                  <input
                                    type="checkbox"
                                    checked={userPerformsMaintenance}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setUserPerformsMaintenance(checked);
                                      if (checked) setSelectedSupplier(null);
                                    }}
                                    className="h-4 w-4 rounded border-neutral-300 text-sa-600 focus:ring-sa-500"
                                  />
                                  <span>أنا من سينفذ الصيانة</span>
                                </label>
                              </span>
                            </div>

                            <div className="info-donation-row">
                              <span className="info-donation-row-label">تاريخ تنفيذ الصيانة:</span>
                              <span className="info-donation-row-value" dir="rtl">
                                <input
                                  id="maintenance-date-form"
                                  type="date"
                                  value={maintenanceDate}
                                  onChange={(e) => setMaintenanceDate(e.target.value)}
                                  min={new Date().toISOString().split("T")[0]}
                                  className="w-full max-w-sm rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
                                />
                              </span>
                            </div>

                            {selectedSupplier && (
                              <div className="info-donation-row col-span-2">
                                <div className="rounded-xl border border-neutral-200 bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800/50 p-4 w-full">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                                      بيانات متعهد الصيانة
                                    </span>

                                    <button
                                      type="button"
                                      className="text-red-600 hover:text-red-700 text-sm underline focus:outline-none"
                                      onClick={() => setSelectedSupplier(null)}
                                    >
                                      إلغاء
                                    </button>
                                  </div>

                                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm" dir="rtl">
                                    <dt className="text-neutral-500 dark:text-neutral-400 info-donation-row-label">
                                      اسم الشركة
                                    </dt>
                                    <dd className="text-neutral-900 dark:text-neutral-100 font-medium info-donation-row-value">
                                      {selectedSupplier.name ?? "—"}
                                    </dd>

                                    <dt className="text-neutral-500 dark:text-neutral-400 info-donation-row-label">
                                      رقم الشركة
                                    </dt>
                                    <dd className="text-neutral-900 dark:text-neutral-100 font-medium info-donation-row-value">
                                      {getSupplierIdSafe(selectedSupplier) ?? "—"}
                                    </dd>

                                    {selectedSupplier.email ? (
                                      <>
                                        <dt className="text-neutral-500 dark:text-neutral-400 info-donation-row-label">
                                          البريد الإلكتروني
                                        </dt>
                                        <dd className="text-neutral-900 dark:text-neutral-100 info-donation-row-value">
                                          {selectedSupplier.email}
                                        </dd>
                                      </>
                                    ) : null}

                                    {selectedSupplier.phone ? (
                                      <>
                                        <dt className="text-neutral-500 dark:text-neutral-400 info-donation-row-label">
                                          رقم الجوال
                                        </dt>
                                        <dd className="text-neutral-900 dark:text-neutral-100 info-donation-row-value">
                                          {selectedSupplier.phone}
                                        </dd>
                                      </>
                                    ) : null}

                                    {selectedSupplier.commercialRegistrationNo ? (
                                      <>
                                        <dt className="text-neutral-500 dark:text-neutral-400 info-donation-row-label">
                                          السجل التجاري
                                        </dt>
                                        <dd className="text-neutral-900 dark:text-neutral-100 info-donation-row-value">
                                          {selectedSupplier.commercialRegistrationNo}
                                        </dd>
                                      </>
                                    ) : null}
                                  </dl>
                                </div>
                              </div>
                            )}

                            <label className="info-donation-confirm-label" dir="rtl">
                              <input
                                type="checkbox"
                                className="info-donation-confirm-checkbox"
                                checked={confirmChecked}
                                onChange={(e) => setConfirmChecked(e.target.checked)}
                              />
                              أقر بأن جميع البيانات والمستندات التي تم إدخالها صحيحة
                            </label>
                          </>
                        )}

                        {submitSuccess && <div className="info-donation-submit-success">{submitSuccess}</div>}

                        {submitError && (
                          <div className="info-donation-submit-error">
                            {submitError}

                            {sessionExpiredOfferRelogin && (
                              <div className="info-donation-session-expired-actions">
                                <button
                                  type="button"
                                  className="info-donation-relogin-btn"
                                  onClick={() => {
                                    clearAuthToken();
                                    window.dispatchEvent(
                                      new CustomEvent("auth:logout", { detail: { reason: "session_expired" } })
                                    );

                                    const returnUrl = location.pathname + (location.search ? location.search : "");
                                    navigate(`/benefaction/login?returnUrl=${encodeURIComponent(returnUrl)}`, {
                                      replace: true,
                                    });
                                  }}
                                >
                                  تسجيل الخروج وإعادة الدخول
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                )}

                {/* باقي التابات (unchanged) */}
                {data && activeSection === "mosque" && detailsTab === "server" && (
                  <div className="info-donation-card info-donation-address-card">
                    <div className="info-donation-address-info">
                      {renderRow("المنطقة", region, 0)}
                      {renderRow("المدينة", city, 1)}
                      {renderRow("المركز", center, 2)}
                      {renderRow("الحي", district, 3)}
                      {renderRow("العنوان الكامل", fullAddress, 4)}
                      {renderRow("قسم النساء", womenSection, 5)}
                      {renderRow("خط العرض", fmt(data.latitude), 6)}
                      {renderRow("خط الطول", fmt(data.longitude), 7)}
                    </div>

                    <div className="info-donation-address-map">
                      <h3 className="info-donation-map-heading">الموقع الجغرافي</h3>

                      <div className="info-donation-map-wrap">
                        {hasCoords ? (
                          <div ref={mapRef} className="info-donation-map" aria-hidden="false" />
                        ) : (
                          <div className="info-donation-map-placeholder">الموقع غير متوفر</div>
                        )}
                      </div>

                      {googleMapsUrl && (
                        <div className="info-donation-map-actions">
                          <a
                            href={googleMapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="info-donation-map-btn"
                          >
                            فتح في خرائط Google
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {data && activeSection === "mosque" && detailsTab === "contact" && (
                  <>
                    {mosqueImagesError && (
                      <div
                        className="info-donation-card"
                        style={{
                          borderColor: "var(--color-red-200, #fecaca)",
                          background: "var(--color-red-50, #fef2f2)",
                        }}
                      >
                        <p className="info-donation-placeholder" style={{ color: "var(--color-red-800, #991b1b)" }}>
                          {mosqueImagesError}
                        </p>
                      </div>
                    )}

                    {loadingMosqueImages && (
                      <div className="info-donation-card">
                        <p className="info-donation-placeholder">جاري تحميل صور المسجد...</p>
                      </div>
                    )}

                    {!loadingMosqueImages && mosqueImages && (
                      <div className="info-donation-mosque-images">
                        <div className="info-donation-card">
                          <h3 className="info-donation-section-title" style={{ fontSize: "1rem", marginBottom: "0.75rem" }}>
                            صورة المسجد الخارجية
                          </h3>

                          {mosqueImages.externalUrls.length === 0 ? (
                            <p className="info-donation-placeholder">لا توجد صورة خارجية</p>
                          ) : (
                            <div className="info-donation-images-grid">
                              {mosqueImages.externalUrls.map((url, i) => (
                                <img
                                  key={i}
                                  src={url}
                                  alt={`صورة المسجد الخارجية ${i + 1}`}
                                  className="info-donation-mosque-img info-donation-mosque-img-clickable"
                                  onClick={() => setLightboxImage(url)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => e.key === "Enter" && setLightboxImage(url)}
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="info-donation-card">
                          <h3 className="info-donation-section-title" style={{ fontSize: "1rem", marginBottom: "0.75rem" }}>
                            صورة المسجد الداخلية
                          </h3>

                          {mosqueImages.internalUrls.length === 0 ? (
                            <p className="info-donation-placeholder">لا توجد صورة داخلية</p>
                          ) : (
                            <div className="info-donation-images-grid">
                              {mosqueImages.internalUrls.map((url, i) => (
                                <img
                                  key={i}
                                  src={url}
                                  alt={`صورة المسجد الداخلية ${i + 1}`}
                                  className="info-donation-mosque-img info-donation-mosque-img-clickable"
                                  onClick={() => setLightboxImage(url)}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => e.key === "Enter" && setLightboxImage(url)}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {!loadingMosqueImages && !mosqueImages && !mosqueImagesError && (
                      <div className="info-donation-card">
                        <p className="info-donation-placeholder">اختر تاب صور المسجد لعرض الصور</p>
                      </div>
                    )}
                  </>
                )}

                {data && activeSection === "mosque" && detailsTab === "otherWork" && (
                  <div className="info-donation-card">
                    {renderRow("الحالة العمرانية", fmt(data.urbanCondition), 0)}
                    {renderRow("تاريخ افتتاح المسجد", fmt(data.mosqueOpeningDate), 1)}
                    {renderRow("مساحة ارض المسجد", fmt(data.landArea), 2)}
                    {renderRow("المساحة المسقوفة من المسجد", fmt(data.roofedArea), 3)}
                    {renderRow("هل يوجد مساحة شاغرة من مساحة المسجد", yesNoBadge(data.isFreeArea), 4)}
                    {renderRow("هل المسجد تاريخي او تراثي", yesNoBadge(data.mosqueHistorical), 5)}
                  </div>
                )}

                {data && activeSection === "mosque" && detailsTab === "address" && (
                  <div className="info-donation-card">
                    {renderRow("عدد صفوف المصليين", fmt(data.fridayPrayerRows), 0)}
                    {renderRow("طول صف مصلى الرجال", fmt(data.lengthRowMenPraying), 1)}
                    {renderRow("عدد صفوف مصلى الرجال", fmt(data.rowMenPrayingNumber), 2)}
                    {renderRow("طول صف مصلى النساء", fmt(data.lengthRowWomenPraying), 3)}
                    {renderRow("عدد صفوف مصلى النساء", fmt(data.rowWomenPrayingNumber), 4)}
                    {renderRow("عدد الابواب الداخلية", fmt(data.internalDoorsNumber), 5)}
                    {renderRow("عدد الابواب الخارجية", fmt(data.externalDoorsNumbers), 6)}
                    {renderRow("عدد منارات المسجد", fmt(data.numMinarets), 7)}
                    {renderRow("عدد الطوابق", fmt(data.numFloors), 8)}
                    {renderRow("هل يوجد بدروم", yesNoBadge(data.hasBasement), 9)}
                    {renderRow("عدد حجرات المسجد", fmt(data.mosqueRooms), 10)}
                    {renderRow("هل توجد طفايات حريق", yesNoBadge(data.hasFireExtinguishers), 11)}
                    {renderRow("هل توجد مضخات نظام الحريق", yesNoBadge(data.hasFireSystemPumps), 12)}
                  </div>
                )}

                {data && activeSection === "mosque" && detailsTab === "qualifications" && (
                  <div className="info-donation-card">
                    {renderRow("عدد دورات المياه للرجال", fmt(data.toiletMenNumber), 0)}
                    {renderRow("عدد دورات المياه للنساء", fmt(data.toiletWomanNumber), 1)}
                  </div>
                )}

                {data && activeSection === "mosque" && detailsTab === "employment" && (
                  <div className="info-donation-card">
                    {renderRow("هل يوجد اجهزة تكيف؟", yesNoBadge(data.hasAirConditioners), 0)}
                    {renderRow("نوع المكيف", fmt(data.acType), 1)}
                    {renderRow("عدد اجهزة التكيف", fmt(data.numAirConditioners), 2)}
                    {renderRow("عدد الاضاءة الداخلية", fmt(data.numLightingInside), 3)}
                    {renderRow("عدد السماعات الداخلية", fmt(data.internalSpeakerNumber), 4)}
                    {renderRow("عدد السماعات الخارجية", fmt(data.externalSpeakerNumber), 5)}
                    {renderRow("هل توجد كاميرا خارجية؟", yesNoBadge(data.hasExternalCamera), 6)}
                    {renderRow("هل توجد كاميرا داخلية؟", yesNoBadge(data.hasInternalCamera), 7)}
                  </div>
                )}

                {data && (
                  <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      className="info-donation-footer-secondary-btn"
                      onClick={() => {
                        if (activeSection === "mosque") gotoSection("mosque");
                        else if (activeSection === "requestDetails") gotoSection("mosque");
                        else gotoSection("requestDetails");
                      }}
                    >
                      السابق
                    </button>

                    {activeSection === "send" ? (
                      <button
                        type="button"
                        className="info-donation-submit-btn"
                        onClick={handleSubmitRequest}
                        disabled={submitActionDisabled}
                      >
                        {submitLoading ? "جاري الإرسال..." : "إرسال الطلب"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="info-donation-submit-btn"
                        onClick={() => {
                          if (activeSection === "mosque") gotoSection("requestDetails");
                          else if (activeSection === "requestDetails") gotoSection("send");
                        }}
                        disabled={activeSection === "requestDetails" ? !requestDetailsReady : false}
                      >
                        {activeSection === "requestDetails" ? "تأكيد الطلب" : "التالي"}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="info-donation-image-lightbox"
          onClick={() => setLightboxImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label="عرض صورة المسجد"
        >
          <button
            type="button"
            className="info-donation-lightbox-close"
            onClick={() => setLightboxImage(null)}
            aria-label="إغلاق"
          >
            ×
          </button>

          <img
            src={lightboxImage}
            alt="صورة المسجد"
            className="info-donation-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Supplier modal */}
      <SupplierModal
        open={supplierModalOpen}
        onClose={() => setSupplierModalOpen(false)}
        onSelectSupplier={(s) => {
          setSelectedSupplier(s);
          setSupplierModalOpen(false);
        }}
      />
    </div>
  );
}